import { pointFrom } from "@excalidraw/math";

import type { LocalPoint } from "@excalidraw/math";

import type { PenPointHandles } from "./types";

/** samples per curved segment — dense enough for smooth arc-length walks */
const SAMPLES_PER_CURVE = 16;

/**
 * Flattened centerline of a path element, for consumers that walk the path
 * by arc length: motion paths (followPath), draw-on/segment windows, flow
 * particles and morph outlines. Pen paths (line elements with bezier
 * `handles`) get each curved segment sampled as a cubic; plain lines,
 * arrows and freedraw return their `points` untouched.
 */
export const getFlattenedPathPoints = (element: {
  points: readonly LocalPoint[];
  handles?: readonly PenPointHandles[] | null;
}): readonly LocalPoint[] => {
  const { points, handles } = element;
  if (!handles || points.length < 2 || !handles.some(Boolean)) {
    return points;
  }
  const out: LocalPoint[] = [points[0]];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const hOut = handles[i]?.out;
    const hIn = handles[i + 1]?.in;
    if (!hOut && !hIn) {
      out.push(p1);
      continue;
    }
    // handles are offsets relative to their anchor
    const c1x = p0[0] + (hOut?.[0] ?? 0);
    const c1y = p0[1] + (hOut?.[1] ?? 0);
    const c2x = p1[0] + (hIn?.[0] ?? 0);
    const c2y = p1[1] + (hIn?.[1] ?? 0);
    for (let s = 1; s <= SAMPLES_PER_CURVE; s++) {
      const t = s / SAMPLES_PER_CURVE;
      const u = 1 - t;
      out.push(
        pointFrom<LocalPoint>(
          u * u * u * p0[0] +
            3 * u * u * t * c1x +
            3 * u * t * t * c2x +
            t * t * t * p1[0],
          u * u * u * p0[1] +
            3 * u * u * t * c1y +
            3 * u * t * t * c2y +
            t * t * t * p1[1],
        ),
      );
    }
  }
  return out;
};
